import { Fragment, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ContentBox,
  ContentPage,
  DrawerFilterPanel,
  DropdownAction,
  FullTable,
  NoResults,
  Notice,
  Select,
  TableFilters,
  Text,
  Tooltip,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import useListFilters from "@hooks/useListFilters";
import { escapeHtml, usePageHeader } from "@components/ow";
import LinkStatus from "@components/LinkStatus";
import { scopeLabel } from "./scope";

// O back manda todos os colaboradores de uma vez: filtro e paginação ficam na tela
const PAGE_SIZE = 25;

// Pílulas do TableFilters: uma por vez, "Todos" sem filtro
const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Com permissão", value: "with" },
  { label: "Sem permissão", value: "without" },
];

const INITIAL_FILTERS = { status: STATUS_DATA[0] };

const BREADCRUMB = [{ page: "Permissões", route: "/c/permissions" }];

// Grupos do catálogo com um segundo filtro, pelo recurso em que o item vale (param na URL)
const RESOURCE_FILTERS = {
  project_hours: {
    param: "projeto",
    any: "Qualquer projeto",
    label: "Projeto",
    empty: "Nenhum projeto encontrado",
  },
  confidential_data: {
    param: "confidencial",
    any: "Qualquer grupo",
    label: "Grupo confidencial",
    empty: "Nenhum grupo encontrado",
  },
};

// Valor antigo do ?grupo= para "com alguma permissão": hoje é a pílula "Com permissão"
const LEGACY_ANY = "any";

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
`;

const Items = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: ${({ theme }) => theme.fontSizes.large_x};
  color: ${({ theme }) => theme.colors.N900};
`;

const Help = styled.span`
  cursor: help;
  text-decoration: underline dotted;
`;

/**
 * Permissões por colaborador (só ADM): lista os EMP da empresa com um resumo
 * dos itens do catálogo que cada um tem. A edição fica em /c/permissions/:userId.
 * Grupo e recurso ficam na URL (?grupo=project_hours&projeto=<id>,
 * ?grupo=confidential_data&confidencial=<id>), usados pelos links do projeto, do
 * ponto e do grupo confidencial.
 */
export default function Permissions() {
  const userPermissions = useService("userPermissions");
  const projectService = useService("projects");
  const groupsService = useService("confidentialGroups");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState({ groups: [], items: [] });
  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [confidentialGroups, setConfidentialGroups] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });

  const {
    storeFilters,
    filters,
    filter,
    openFilter,
    setOpenFilter,
    handleSetFilters,
    handleClearFilters,
    hasFilter,
  } = useListFilters(INITIAL_FILTERS, setPagination);

  usePageHeader({ title: "Permissões", breadcrumb: BREADCRUMB });

  const rawGroup = searchParams.get("grupo") || "";
  const group = rawGroup === LEGACY_ANY ? "" : rawGroup;
  const resourceFilter = RESOURCE_FILTERS[group] || null;
  const resourceId = resourceFilter ? Number(searchParams.get(resourceFilter.param)) || null : null;
  const statusValue = filters.status?.value || "";
  const searchTerm = (filters.search || "").trim().toLowerCase();
  // Busca (do useListFilters) mais grupo e recurso (da URL)
  const filterCount = hasFilter + (group ? 1 : 0) + (resourceId ? 1 : 0);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Link antigo "com alguma permissão": vira a pílula e sai da URL
  useEffect(() => {
    if (rawGroup !== LEGACY_ANY) return;
    storeFilters.setFilters({ status: STATUS_DATA[1] });
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawGroup]);

  // Filtro novo vindo da URL (drawer, link de outra tela) volta para a primeira página
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [group, resourceId]);

  async function load() {
    setLoading(true);
    try {
      const [catalogResp, usersResp, projectsResp, groupsResp] = await Promise.all([
        userPermissions.catalog(),
        userPermissions.listUsers(),
        projectService.list("limit=9999&order=name:ASC"),
        groupsService.listGroups(),
      ]);
      setCatalog(catalogResp?.data || { groups: [], items: [] });
      setPeople(usersResp?.data || []);
      setProjects((projectsResp?.data?.list || []).map((p) => ({ value: p.id, label: p.name })));
      setConfidentialGroups((groupsResp?.data || []).map((g) => ({ value: g.id, label: g.name })));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao carregar permissões");
    } finally {
      setLoading(false);
    }
  }

  const itemsByKey = useMemo(() => new Map(catalog.items.map((item) => [item.key, item])), [catalog]);

  const groupOptions = useMemo(() => catalog.groups.map((g) => ({ value: g.key, label: g.label })), [catalog]);

  const filtered = useMemo(
    () =>
      people.filter(({ user, items }) => {
        if (statusValue === "with" && items.length === 0) return false;
        if (statusValue === "without" && items.length > 0) return false;
        if (group) {
          const inGroup = items.filter((i) => itemsByKey.get(i.key)?.group === group);
          if (inGroup.length === 0) return false;
          // Item vale no recurso (projeto, grupo) quando é de todos ou inclui o id
          if (resourceId && !inGroup.some((i) => i.resource_ids === null || i.resource_ids.includes(resourceId))) {
            return false;
          }
        }
        if (!searchTerm) return true;
        return (user.name || "").toLowerCase().includes(searchTerm) || (user.email || "").toLowerCase().includes(searchTerm);
      }),
    [people, statusValue, searchTerm, group, resourceId, itemsByKey]
  );

  const pageRows = filtered.slice(
    pagination.pageIndex * pagination.pageSize,
    (pagination.pageIndex + 1) * pagination.pageSize
  );

  // O drawer abre com o que está aplicado (na URL)
  function openFilterPanel() {
    handleSetFilters(null, "group", group);
    handleSetFilters(null, "resource", resourceId);
    setOpenFilter(true);
  }

  function confirmFilter() {
    const next = {};
    if (filter.group) next.grupo = filter.group;
    const draft = RESOURCE_FILTERS[filter.group];
    if (draft && filter.resource) next[draft.param] = String(filter.resource);
    setSearchParams(next, { replace: true });
    setOpenFilter(false);
  }

  // Limpa a busca, o grupo e o recurso (a pílula fica, como nas outras listas)
  function clearFilters() {
    handleClearFilters();
    setSearchParams({}, { replace: true });
  }

  // O "Limpar filtros" do vazio volta também para "Todos"
  function clearAll() {
    clearFilters();
    storeFilters.setFilters({ status: STATUS_DATA[0] });
  }

  function openUser(userId) {
    navigate(`/c/permissions/${userId}`, { state: { listSearch: location.search } });
  }

  function renderScope(item, catalogItem) {
    const scope = scopeLabel(item, catalogItem);
    if (!scope.title) return ` (${scope.text.toLowerCase()})`;
    if (scope.text === scope.title) return ` (${scope.text})`;
    return (
      <>
        {" "}
        <Tooltip content={scope.title} $position="top" $size="medium" $color="dark">
          <Help>({scope.text})</Help>
        </Tooltip>
      </>
    );
  }

  function renderSummary(items) {
    if (items.length === 0) {
      return <Text $text="Sem permissões" $variant="paragraph_small_medium" $color="N400" />;
    }
    return (
      <Summary>
        {catalog.groups.map((g) => {
          const groupItems = items.filter((i) => itemsByKey.get(i.key)?.group === g.key);
          if (groupItems.length === 0) return null;
          return (
            <div key={g.key}>
              <Text $text={g.label} $variant="caption_medium" $color="N400" />
              <Items>
                {groupItems.map((i, index) => {
                  const item = itemsByKey.get(i.key);
                  return (
                    <Fragment key={i.key}>
                      {index > 0 ? " · " : null}
                      {item.label}
                      {item.scopable ? renderScope(i, item) : null}
                    </Fragment>
                  );
                })}
              </Items>
            </div>
          );
        })}
      </Summary>
    );
  }

  const columns = [
    {
      id: "employee",
      header: "Colaborador",
      size: 280,
      maxSize: 340,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.user.name || "-")} $variant="paragraph_small_bold" $color="N900" />
          <Text $text={escapeHtml(row.original.user.email || "")} $variant="caption_medium" $color="N400" />
        </div>
      ),
    },
    { id: "status", header: "Situação", size: 220, cell: ({ row }) => <LinkStatus status={row.original.link_status} /> },
    { id: "permissions", header: "Permissões", size: 560, cell: ({ row }) => renderSummary(row.original.items) },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => (
        <DropdownAction options={[{ label: "Editar permissões", action: () => openUser(row.original.user.id) }]} />
      ),
    },
  ];

  const noResults =
    people.length === 0 ? (
      <NoResults notResultText="Nenhum colaborador na empresa" />
    ) : (
      <NoResults notResultText="Nenhum colaborador encontrado" includeText="Limpar filtros" includeAction={clearAll} />
    );

  const draftFilter = RESOURCE_FILTERS[filter.group] || null;
  const draftOptions = filter.group === "confidential_data" ? confidentialGroups : projects;
  const draftResource = draftOptions.find((o) => o.value === filter.resource) || null;

  return (
    <ContentPage>
      <Notice
        $variant="gray"
        $text="Defina o que cada colaborador pode ver e fazer além do próprio acesso. Administradores sempre têm acesso total e não aparecem aqui."
      />

      <ContentBox>
        <TableFilters
          storeFilters={storeFilters}
          searchTerm="search"
          searchPlaceholder="Buscar por nome ou e-mail"
          statusData={STATUS_DATA}
          filter={filter}
          handleSetFilters={handleSetFilters}
          handleClearFilters={clearFilters}
          setStatePagination={setPagination}
          openFilter={openFilterPanel}
          disableExport
          hasFilter={filterCount}
        />

        <FullTable
          data={pageRows}
          columns={columns}
          rowCount={filtered.length}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.user.id)}
          isLoading={loading}
          onRowClick={(row) => openUser(row.user.id)}
          noResults={noResults}
        />
      </ContentBox>

      <DrawerFilterPanel
        $showDrawer={openFilter}
        $setShowDrawer={() => setOpenFilter((prev) => !prev)}
        $confirmFilter={confirmFilter}
        $clearFilters={clearFilters}
        $hasFilter={filterCount}
      >
        <Select
          $idForm="permissions-group"
          $label="Permissão em"
          $isClearable
          $options={groupOptions}
          placeholder="Qualquer grupo"
          value={groupOptions.find((o) => o.value === filter.group) || null}
          onChange={(option) => {
            handleSetFilters(null, "group", option ? option.value : "");
            handleSetFilters(null, "resource", null);
          }}
        />
        {draftFilter ? (
          <Select
            $idForm="permissions-resource"
            $label={draftFilter.label}
            $isClearable
            $options={draftOptions}
            placeholder={draftFilter.any}
            $textNoOptions={draftFilter.empty}
            value={draftResource}
            onChange={(option) => handleSetFilters(null, "resource", option ? option.value : null)}
          />
        ) : null}
      </DrawerFilterPanel>
    </ContentPage>
  );
}
